// Platform API Integration Services
import { supabase } from '@/config/supabase';

// Typy dla API
interface FacebookPageToken {
  access_token: string;
  page_id: string;
  page_name: string;
}

interface LinkedInProfile {
  access_token: string;
  profile_id: string;
  organization_id?: string;
}

interface InstagramAccount {
  access_token: string;
  instagram_business_account: string;
  username: string;
}

interface PostMetrics {
  impressions: number;
  reach: number;
  engagement: number;
  clicks: number;
  likes: number;
  comments: number;
  shares: number;
}

// Facebook Graph API
export class FacebookAPI {
  private baseUrl = 'https://graph.facebook.com/v18.0';
  
  constructor(private accessToken: string) {}

  // Autoryzacja OAuth
  static getAuthUrl(redirectUri: string): string {
    const appId = import.meta.env.VITE_FACEBOOK_APP_ID || '';
    const scope = 'pages_manage_posts,pages_read_engagement,pages_show_list,instagram_basic,instagram_content_publish';
    return `https://www.facebook.com/v18.0/dialog/oauth?client_id=${appId}&redirect_uri=${redirectUri}&scope=${scope}&response_type=code`;
  }

  // Wymiana kodu na token
  static async exchangeCodeForToken(code: string, redirectUri: string): Promise<string> {
    const appId = import.meta.env.VITE_FACEBOOK_APP_ID || '';
    const appSecret = import.meta.env.VITE_FACEBOOK_APP_SECRET || '';
    
    const response = await fetch(
      `https://graph.facebook.com/v18.0/oauth/access_token?client_id=${appId}&client_secret=${appSecret}&code=${code}&redirect_uri=${redirectUri}`
    );
    
    const data = await response.json();
    return data.access_token;
  }

  // Pobierz strony użytkownika
  async getPages(): Promise<FacebookPageToken[]> {
    const response = await fetch(
      `${this.baseUrl}/me/accounts?access_token=${this.accessToken}`
    );
    
    const data = await response.json();
    return data.data.map((page: any) => ({
      access_token: page.access_token,
      page_id: page.id,
      page_name: page.name
    }));
  }

  // Publikuj post
  async publishPost(
    pageId: string,
    pageAccessToken: string,
    message: string,
    imageUrl?: string,
    scheduledTime?: Date
  ): Promise<{ id: string; success: boolean }> {
    const params: any = {
      message,
      access_token: pageAccessToken
    };

    if (imageUrl) {
      params.link = imageUrl;
    }

    if (scheduledTime && scheduledTime > new Date()) {
      params.published = false;
      params.scheduled_publish_time = Math.floor(scheduledTime.getTime() / 1000);
    }

    const response = await fetch(
      `${this.baseUrl}/${pageId}/feed`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params)
      }
    );

    const data = await response.json();
    return {
      id: data.id,
      success: !data.error
    };
  }

  // Pobierz statystyki posta
  async getPostInsights(postId: string, pageAccessToken: string): Promise<PostMetrics> {
    const response = await fetch(
      `${this.baseUrl}/${postId}/insights?metric=post_impressions,post_engaged_users,post_clicks,post_reactions_by_type_total&access_token=${pageAccessToken}`
    );

    const data = await response.json();
    const metrics: PostMetrics = {
      impressions: 0,
      reach: 0,
      engagement: 0,
      clicks: 0,
      likes: 0,
      comments: 0,
      shares: 0
    };

    data.data.forEach((metric: any) => {
      switch (metric.name) {
        case 'post_impressions':
          metrics.impressions = metric.values[0].value;
          break;
        case 'post_engaged_users':
          metrics.engagement = metric.values[0].value;
          break;
        case 'post_clicks':
          metrics.clicks = metric.values[0].value;
          break;
        case 'post_reactions_by_type_total':
          metrics.likes = Object.values(metric.values[0].value).reduce((a: any, b: any) => a + b, 0);
          break;
      }
    });

    return metrics;
  }
}

// LinkedIn API
export class LinkedInAPI {
  private baseUrl = 'https://api.linkedin.com/v2';
  
  constructor(private accessToken: string) {}

  // Autoryzacja OAuth
  static getAuthUrl(redirectUri: string): string {
    const clientId = import.meta.env.VITE_LINKEDIN_CLIENT_ID || '';
    const scope = 'r_liteprofile r_emailaddress w_member_social w_organization_social rw_organization_admin';
    return `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}`;
  }

  // Wymiana kodu na token - przez backend proxy
  static async exchangeCodeForToken(code: string, redirectUri: string): Promise<string> {
    try {
      // Używamy backendu jako proxy aby uniknąć CORS
      const backendUrl = import.meta.env.VITE_API_URL || 'https://api.plexisystem.pl';
      
      const response = await fetch(`${backendUrl}/api/social-media/linkedin/exchange-token`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
        },
        body: JSON.stringify({
          code,
          redirect_uri: redirectUri,
          client_id: import.meta.env.VITE_LINKEDIN_CLIENT_ID,
          client_secret: import.meta.env.VITE_LINKEDIN_CLIENT_SECRET
        })
      });
      
      if (!response.ok) {
        const error = await response.text();
        throw new Error(`Backend error: ${error}`);
      }
      
      const data = await response.json();
      return data.access_token;
    } catch (error) {
      console.error('Error exchanging LinkedIn code:', error);
      // Fallback - spróbuj bezpośrednio (nie zadziała przez CORS, ale dla debugowania)
      throw error;
    }
  }

  // Pobierz profil - przez backend proxy
  async getProfile(): Promise<LinkedInProfile> {
    // Używamy backendu jako proxy aby uniknąć CORS
    const backendUrl = import.meta.env.VITE_API_URL || 'https://api.plexisystem.pl';
    
    const response = await fetch(`${backendUrl}/api/social-media/linkedin/profile`, {
      headers: {
        'Authorization': `Bearer ${this.accessToken}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch LinkedIn profile');
    }
    
    const data = await response.json();
    return {
      access_token: this.accessToken,
      profile_id: data.sub || data.id,
      organization_id: data.organizationId
    };
  }

  // Publikuj post - przez backend proxy
  async publishPost(
    authorId: string,
    message: string,
    imageUrl?: string,
    isOrganization: boolean = false
  ): Promise<{ id: string; success: boolean }> {
    // Używamy backendu jako proxy aby uniknąć CORS
    const backendUrl = import.meta.env.VITE_API_URL || 'https://api.plexisystem.pl';
    
    const response = await fetch(`${backendUrl}/api/social-media/linkedin/publish`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
      },
      body: JSON.stringify({
        content: message,
        access_token: this.accessToken,
        author_id: authorId,
        is_organization: isOrganization
      })
    });
    
    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to publish to LinkedIn: ${error}`);
    }
    
    const data = await response.json();
    return {
      id: data.post_id,
      success: data.success
    };
  }

  // Upload obrazu
  private async uploadImage(authorId: string, isOrganization: boolean): Promise<any> {
    const author = isOrganization 
      ? `urn:li:organization:${authorId}`
      : `urn:li:person:${authorId}`;

    const registerUpload = {
      registerUploadRequest: {
        recipes: ['urn:li:digitalmediaRecipe:feedshare-image'],
        owner: author,
        serviceRelationships: [{
          relationshipType: 'OWNER',
          identifier: 'urn:li:userGeneratedContent'
        }]
      }
    };

    const response = await fetch(`${this.baseUrl}/assets?action=registerUpload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.accessToken}`,
        'Content-Type': 'application/json',
        'X-Restli-Protocol-Version': '2.0.0'
      },
      body: JSON.stringify(registerUpload)
    });

    return await response.json();
  }

  // Pobierz statystyki
  async getPostAnalytics(postId: string): Promise<PostMetrics> {
    const response = await fetch(
      `${this.baseUrl}/socialActions/${postId}?projection=(likes,comments,shares)`,
      {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'X-Restli-Protocol-Version': '2.0.0'
        }
      }
    );

    const data = await response.json();
    return {
      impressions: data.impressions || 0,
      reach: data.reach || 0,
      engagement: data.engagement || 0,
      clicks: data.clicks || 0,
      likes: data.likes || 0,
      comments: data.comments || 0,
      shares: data.shares || 0
    };
  }
}

// Instagram Basic Display API
export class InstagramAPI {
  private baseUrl = 'https://graph.instagram.com';
  
  constructor(private accessToken: string) {}

  // Autoryzacja przez Facebook
  static async getInstagramAccount(facebookPageId: string, pageAccessToken: string): Promise<InstagramAccount> {
    const response = await fetch(
      `https://graph.facebook.com/v18.0/${facebookPageId}?fields=instagram_business_account&access_token=${pageAccessToken}`
    );
    
    const data = await response.json();
    
    if (data.instagram_business_account) {
      const igResponse = await fetch(
        `https://graph.facebook.com/v18.0/${data.instagram_business_account.id}?fields=username&access_token=${pageAccessToken}`
      );
      const igData = await igResponse.json();
      
      return {
        access_token: pageAccessToken,
        instagram_business_account: data.instagram_business_account.id,
        username: igData.username
      };
    }
    
    throw new Error('No Instagram account connected to this Facebook page');
  }

  // Publikuj post (przez Facebook API)
  async publishPost(
    igAccountId: string,
    caption: string,
    imageUrl: string,
    hashtags: string[] = []
  ): Promise<{ id: string; success: boolean }> {
    const fullCaption = `${caption}\n\n${hashtags.map(tag => `#${tag}`).join(' ')}`;
    
    // Krok 1: Utwórz media container
    const createResponse = await fetch(
      `${this.baseUrl}/${igAccountId}/media`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image_url: imageUrl,
          caption: fullCaption,
          access_token: this.accessToken
        })
      }
    );
    
    const createData = await createResponse.json();
    
    if (!createData.id) {
      return { id: '', success: false };
    }
    
    // Krok 2: Publikuj media
    const publishResponse = await fetch(
      `${this.baseUrl}/${igAccountId}/media_publish`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          creation_id: createData.id,
          access_token: this.accessToken
        })
      }
    );
    
    const publishData = await publishResponse.json();
    
    return {
      id: publishData.id,
      success: !publishData.error
    };
  }

  // Pobierz statystyki
  async getMediaInsights(mediaId: string): Promise<PostMetrics> {
    const response = await fetch(
      `${this.baseUrl}/${mediaId}/insights?metric=impressions,reach,engagement,saved&access_token=${this.accessToken}`
    );
    
    const data = await response.json();
    const metrics: PostMetrics = {
      impressions: 0,
      reach: 0,
      engagement: 0,
      clicks: 0,
      likes: 0,
      comments: 0,
      shares: 0
    };
    
    data.data.forEach((metric: any) => {
      switch (metric.name) {
        case 'impressions':
          metrics.impressions = metric.values[0].value;
          break;
        case 'reach':
          metrics.reach = metric.values[0].value;
          break;
        case 'engagement':
          metrics.engagement = metric.values[0].value;
          break;
      }
    });
    
    return metrics;
  }
}

// Twitter/X API v2
export class TwitterAPI {
  private baseUrl = 'https://api.twitter.com/2';
  
  constructor(private bearerToken: string) {}

  // OAuth 2.0 PKCE flow
  static getAuthUrl(redirectUri: string, codeChallenge: string): string {
    const clientId = import.meta.env.VITE_TWITTER_CLIENT_ID || '';
    const scope = 'tweet.read tweet.write users.read offline.access';
    return `https://twitter.com/i/oauth2/authorize?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}&code_challenge=${codeChallenge}&code_challenge_method=S256`;
  }

  // Publikuj tweet
  async publishTweet(text: string, mediaIds?: string[]): Promise<{ id: string; success: boolean }> {
    const body: any = { text };
    
    if (mediaIds && mediaIds.length > 0) {
      body.media = { media_ids: mediaIds };
    }
    
    const response = await fetch(`${this.baseUrl}/tweets`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.bearerToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    });
    
    const data = await response.json();
    
    return {
      id: data.data?.id || '',
      success: !data.errors
    };
  }

  // Pobierz statystyki
  async getTweetMetrics(tweetId: string): Promise<PostMetrics> {
    const response = await fetch(
      `${this.baseUrl}/tweets/${tweetId}?tweet.fields=public_metrics`,
      {
        headers: {
          'Authorization': `Bearer ${this.bearerToken}`
        }
      }
    );
    
    const data = await response.json();
    const metrics = data.data?.public_metrics || {};
    
    return {
      impressions: metrics.impression_count || 0,
      reach: 0, // Twitter nie udostępnia reach
      engagement: (metrics.like_count || 0) + (metrics.retweet_count || 0) + (metrics.reply_count || 0),
      clicks: metrics.url_link_clicks || 0,
      likes: metrics.like_count || 0,
      comments: metrics.reply_count || 0,
      shares: metrics.retweet_count || 0
    };
  }
}

// Główny serwis zarządzający wszystkimi platformami
export class SocialMediaAPIService {
  private apis: Map<string, any> = new Map();

  constructor() {
    this.loadStoredTokens();
  }

  // Wczytaj zapisane tokeny
  private async loadStoredTokens() {
    try {
      // Najpierw spróbuj wczytać z localStorage
      const localTokens = JSON.parse(localStorage.getItem('social_tokens') || '{}');
      
      if (localTokens.facebook?.token) {
        this.apis.set('facebook', new FacebookAPI(localTokens.facebook.token));
        console.log('✅ Facebook token loaded from localStorage');
      }
      if (localTokens.linkedin?.token) {
        this.apis.set('linkedin', new LinkedInAPI(localTokens.linkedin.token));
        console.log('✅ LinkedIn token loaded from localStorage');
      }
      if (localTokens.instagram?.token) {
        this.apis.set('instagram', new InstagramAPI(localTokens.instagram.token));
        console.log('✅ Instagram token loaded from localStorage');
      }
      if (localTokens.twitter?.token) {
        this.apis.set('twitter', new TwitterAPI(localTokens.twitter.token));
        console.log('✅ Twitter token loaded from localStorage');
      }

      // Opcjonalnie spróbuj wczytać z Supabase
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: tokens, error } = await supabase
            .from('social_media_tokens')
            .select('*')
            .eq('user_id', user.id)
            .single();

          if (!error && tokens) {
            // Aktualizuj localStorage jeśli Supabase ma nowsze dane
            const updatedTokens = { ...localTokens };
            
            if (tokens.facebook_token && !localTokens.facebook?.token) {
              this.apis.set('facebook', new FacebookAPI(tokens.facebook_token));
              updatedTokens.facebook = { token: tokens.facebook_token, data: tokens.facebook_data };
            }
            if (tokens.linkedin_token && !localTokens.linkedin?.token) {
              this.apis.set('linkedin', new LinkedInAPI(tokens.linkedin_token));
              updatedTokens.linkedin = { token: tokens.linkedin_token, data: tokens.linkedin_data };
            }
            if (tokens.instagram_token && !localTokens.instagram?.token) {
              this.apis.set('instagram', new InstagramAPI(tokens.instagram_token));
              updatedTokens.instagram = { token: tokens.instagram_token, data: tokens.instagram_data };
            }
            if (tokens.twitter_token && !localTokens.twitter?.token) {
              this.apis.set('twitter', new TwitterAPI(tokens.twitter_token));
              updatedTokens.twitter = { token: tokens.twitter_token, data: tokens.twitter_data };
            }
            
            localStorage.setItem('social_tokens', JSON.stringify(updatedTokens));
          }
        }
      } catch (supabaseError) {
        console.warn('Could not load from Supabase, using local storage only');
      }
    } catch (error) {
      console.warn('Error loading tokens:', error);
    }
  }

  // Zapisz token
  async saveToken(platform: string, token: string, additionalData?: any) {
    try {
      // Zapisz lokalnie w localStorage
      const storedTokens = JSON.parse(localStorage.getItem('social_tokens') || '{}');
      storedTokens[platform] = {
        token: token,
        data: additionalData,
        saved_at: new Date().toISOString()
      };
      localStorage.setItem('social_tokens', JSON.stringify(storedTokens));
      console.log(`✅ Token for ${platform} saved locally`);

      // Opcjonalnie spróbuj zapisać do Supabase (jeśli jest połączenie)
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const updateData: any = {
            user_id: user.id,
            [`${platform}_token`]: token,
            [`${platform}_data`]: additionalData,
            updated_at: new Date().toISOString()
          };

          await supabase
            .from('social_media_tokens')
            .upsert(updateData);
          console.log(`✅ Token for ${platform} saved to Supabase`);
        }
      } catch (supabaseError) {
        console.warn('Could not save to Supabase, using local storage only:', supabaseError);
      }

      // Zainicjalizuj API
      switch (platform) {
        case 'facebook':
          this.apis.set('facebook', new FacebookAPI(token));
          break;
        case 'linkedin':
          this.apis.set('linkedin', new LinkedInAPI(token));
          break;
        case 'instagram':
          this.apis.set('instagram', new InstagramAPI(token));
          break;
        case 'twitter':
          this.apis.set('twitter', new TwitterAPI(token));
          break;
      }
      
      return { success: true };
    } catch (error) {
      console.error(`Failed to save token for ${platform}:`, error);
      throw error;
    }
  }

  // Pobierz API dla platformy
  getAPI(platform: string) {
    return this.apis.get(platform);
  }

  // Publikuj na wszystkich platformach
  async publishToAll(
    content: string,
    platforms: string[],
    imageUrl?: string,
    scheduledTime?: Date
  ): Promise<Record<string, { id: string; success: boolean; error?: string }>> {
    const results: Record<string, { id: string; success: boolean; error?: string }> = {};

    for (const platform of platforms) {
      try {
        const api = this.getAPI(platform);
        if (!api) {
          results[platform] = { 
            id: '', 
            success: false, 
            error: 'Platform not connected' 
          };
          continue;
        }

        let result;
        switch (platform) {
          case 'facebook':
            // Pobierz pierwszą stronę (możesz rozszerzyć o wybór strony)
            const pages = await api.getPages();
            if (pages.length > 0) {
              result = await api.publishPost(
                pages[0].page_id,
                pages[0].access_token,
                content,
                imageUrl,
                scheduledTime
              );
            }
            break;

          case 'linkedin':
            const profile = await api.getProfile();
            result = await api.publishPost(
              profile.organization_id || profile.profile_id,
              content,
              imageUrl,
              !!profile.organization_id
            );
            break;

          case 'instagram':
            if (imageUrl) {
              // Instagram wymaga obrazu
              result = await api.publishPost(
                '', // ID konta Instagram
                content,
                imageUrl,
                this.extractHashtags(content)
              );
            } else {
              result = { 
                id: '', 
                success: false, 
                error: 'Instagram requires an image' 
              };
            }
            break;

          case 'twitter':
            result = await api.publishTweet(content);
            break;
        }

        results[platform] = result || { id: '', success: false };
      } catch (error) {
        console.error(`Error publishing to ${platform}:`, error);
        results[platform] = { 
          id: '', 
          success: false, 
          error: error instanceof Error ? error.message : 'Unknown error' 
        };
      }
    }

    return results;
  }

  // Pobierz zbiorcze statystyki
  async getAggregatedMetrics(postIds: Record<string, string>): Promise<PostMetrics> {
    const aggregated: PostMetrics = {
      impressions: 0,
      reach: 0,
      engagement: 0,
      clicks: 0,
      likes: 0,
      comments: 0,
      shares: 0
    };

    for (const [platform, postId] of Object.entries(postIds)) {
      try {
        const api = this.getAPI(platform);
        if (!api) continue;

        let metrics;
        switch (platform) {
          case 'facebook':
            // Potrzebujemy page access token
            const pages = await api.getPages();
            if (pages.length > 0) {
              metrics = await api.getPostInsights(postId, pages[0].access_token);
            }
            break;

          case 'linkedin':
            metrics = await api.getPostAnalytics(postId);
            break;

          case 'instagram':
            metrics = await api.getMediaInsights(postId);
            break;

          case 'twitter':
            metrics = await api.getTweetMetrics(postId);
            break;
        }

        if (metrics) {
          aggregated.impressions += metrics.impressions;
          aggregated.reach += metrics.reach;
          aggregated.engagement += metrics.engagement;
          aggregated.clicks += metrics.clicks;
          aggregated.likes += metrics.likes;
          aggregated.comments += metrics.comments;
          aggregated.shares += metrics.shares;
        }
      } catch (error) {
        console.error(`Error fetching metrics for ${platform}:`, error);
      }
    }

    return aggregated;
  }

  // Pomocnicza funkcja do wyciągania hashtagów
  private extractHashtags(content: string): string[] {
    const regex = /#\w+/g;
    const matches = content.match(regex);
    return matches ? matches.map(tag => tag.substring(1)) : [];
  }
}

// Eksportuj singleton
export const socialMediaAPI = new SocialMediaAPIService();
